import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { adminEditSubmission } from "@/lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Video, Check } from "lucide-react";

/**
 * Field-by-field editor for a JotForm-derived submission.
 * - Top: text fields populated from the JotForm submission
 * - Bottom: image gallery (click to set as hero, shift-click adds to thumbnails)
 *           + video gallery (click to set as primary video)
 */
export default function SubmissionEditDialog({ submission, onClose, onSaved }) {
    const [form, setForm] = useState(submission);

    useEffect(() => setForm(submission), [submission]);
    if (!submission) return null;

    const setField = (k) => (e) =>
        setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

    const setHero = (url) =>
        setForm((f) => {
            const t = (f.thumbnails || []).filter((u) => u !== url);
            return { ...f, image_url: url, thumbnails: t };
        });

    const toggleThumb = (url) =>
        setForm((f) => {
            if (url === f.image_url) return f;
            const t = f.thumbnails || [];
            const has = t.includes(url);
            const next = has
                ? t.filter((u) => u !== url)
                : [...t, url].slice(0, 3);
            return { ...f, thumbnails: next };
        });

    const setVideo = (url) =>
        setForm((f) => ({ ...f, video_url: f.video_url === url ? "" : url }));

    const save = async () => {
        try {
            await adminEditSubmission(submission.id, {
                contact_name: form.contact_name,
                email: form.email,
                phone: form.phone,
                business: form.business,
                website: form.website,
                title: form.title,
                sub_header: form.sub_header,
                blurb: form.blurb,
                body: form.body,
                image_url: form.image_url,
                thumbnails: form.thumbnails || [],
                video_url: form.video_url || "",
                which_promotion: form.which_promotion,
            });
            toast.success("Submission saved.");
            onSaved?.();
            onClose?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        }
    };

    const thumbs = form.thumbnails || [];
    const allImages = form.image_urls || [];
    const allVideos = form.video_urls || [];

    return (
        <Dialog open onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent
                className="bg-white text-[#1B1B1B] sm:max-w-3xl max-h-[88vh] overflow-y-auto"
                data-testid="submission-edit-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="font-poppins text-2xl text-[#E63B26]">
                        Edit submission
                    </DialogTitle>
                    <DialogDescription className="font-sofia">
                        Tune the fields below before approving. They'll be used
                        verbatim when the Post is published.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div>
                        <Label>Contact name</Label>
                        <Input
                            value={form.contact_name || ""}
                            onChange={setField("contact_name")}
                            data-testid="edit-contact_name"
                        />
                    </div>
                    <div>
                        <Label>Email</Label>
                        <Input
                            value={form.email || ""}
                            onChange={setField("email")}
                            data-testid="edit-email"
                        />
                    </div>
                    <div>
                        <Label>Phone</Label>
                        <Input
                            value={form.phone || ""}
                            onChange={setField("phone")}
                        />
                    </div>
                    <div>
                        <Label>Business / Venue</Label>
                        <Input
                            value={form.business || ""}
                            onChange={setField("business")}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Website</Label>
                        <Input
                            value={form.website || ""}
                            onChange={setField("website")}
                        />
                    </div>
                    <div>
                        <Label>Instagram</Label>
                        <Input
                            value={form.instagram || ""}
                            onChange={setField("instagram")}
                            placeholder="@handle or URL"
                            data-testid="edit-instagram"
                        />
                    </div>
                    <div>
                        <Label>Facebook</Label>
                        <Input
                            value={form.facebook || ""}
                            onChange={setField("facebook")}
                            placeholder="page name or URL"
                            data-testid="edit-facebook"
                        />
                    </div>
                    <div>
                        <Label>X (Twitter)</Label>
                        <Input
                            value={form.twitter || ""}
                            onChange={setField("twitter")}
                            placeholder="@handle or URL"
                            data-testid="edit-twitter"
                        />
                    </div>
                    <div>
                        <Label>Bluesky</Label>
                        <Input
                            value={form.bluesky || ""}
                            onChange={setField("bluesky")}
                            placeholder="handle.bsky.social or URL"
                            data-testid="edit-bluesky"
                        />
                    </div>
                    <div>
                        <Label>Snapchat</Label>
                        <Input
                            value={form.snapchat || ""}
                            onChange={setField("snapchat")}
                            placeholder="username or URL"
                            data-testid="edit-snapchat"
                        />
                    </div>
                    <div>
                        <Label>Threads</Label>
                        <Input
                            value={form.threads || ""}
                            onChange={setField("threads")}
                            placeholder="@handle or URL"
                            data-testid="edit-threads"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Other social</Label>
                        <Input
                            value={form.other_social || ""}
                            onChange={setField("other_social")}
                            placeholder="full URL (e.g. https://tiktok.com/@you)"
                            data-testid="edit-other_social"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Title *</Label>
                        <Input
                            value={form.title || ""}
                            onChange={setField("title")}
                            data-testid="edit-title"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Sub-header</Label>
                        <Input
                            value={form.sub_header || ""}
                            onChange={setField("sub_header")}
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Blurb (≤100 chars, shown on Latest Posts)</Label>
                        <Input
                            value={form.blurb || ""}
                            onChange={setField("blurb")}
                            maxLength={140}
                            data-testid="edit-blurb"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <Label>Body</Label>
                        <Textarea
                            rows={8}
                            value={form.body || ""}
                            onChange={setField("body")}
                            data-testid="edit-body"
                        />
                    </div>
                </div>

                {/* Image gallery */}
                <div className="mt-6">
                    <h4 className="font-poppins font-semibold text-sm tracking-[0.18em] uppercase text-[#1B1B1B]/80 mb-2 flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" /> Uploaded images ({allImages.length})
                    </h4>
                    {allImages.length === 0 ? (
                        <p className="font-sofia text-xs text-[#1B1B1B]/50 italic">
                            No images attached to this submission.
                        </p>
                    ) : (
                        <>
                            <p className="font-sofia text-xs text-[#1B1B1B]/60 mb-2">
                                Click an image to set it as the hero. Shift+click
                                to add up to 3 thumbnails.
                            </p>
                            <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                                {allImages.map((url) => {
                                    const isHero = url === form.image_url;
                                    const isThumb = thumbs.includes(url);
                                    return (
                                        <button
                                            key={url}
                                            type="button"
                                            onClick={(e) => {
                                                if (e.shiftKey) toggleThumb(url);
                                                else setHero(url);
                                            }}
                                            className={`relative aspect-square overflow-hidden border-2 transition ${
                                                isHero
                                                    ? "border-[#E63B26]"
                                                    : isThumb
                                                      ? "border-[#F5C53D]"
                                                      : "border-transparent hover:border-black/40"
                                            }`}
                                            data-testid={`edit-image-${allImages.indexOf(url)}`}
                                            title={url}
                                        >
                                            <img
                                                src={url}
                                                alt=""
                                                className="w-full h-full object-cover"
                                            />
                                            {isHero && (
                                                <span className="absolute top-1 left-1 bg-[#E63B26] text-white text-[0.55rem] font-poppins tracking-wider uppercase px-1.5 py-0.5">
                                                    Hero
                                                </span>
                                            )}
                                            {isThumb && !isHero && (
                                                <span className="absolute top-1 left-1 bg-[#F5C53D] text-[#1B1B1B] text-[0.55rem] font-poppins tracking-wider uppercase px-1.5 py-0.5">
                                                    Thumb
                                                </span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Video gallery */}
                <div className="mt-6">
                    <h4 className="font-poppins font-semibold text-sm tracking-[0.18em] uppercase text-[#1B1B1B]/80 mb-2 flex items-center gap-2">
                        <Video className="h-4 w-4" /> Uploaded videos ({allVideos.length})
                    </h4>
                    {allVideos.length === 0 ? (
                        <p className="font-sofia text-xs text-[#1B1B1B]/50 italic">
                            No videos attached.
                        </p>
                    ) : (
                        <div className="space-y-2">
                            {allVideos.map((url) => {
                                const active = url === form.video_url;
                                return (
                                    <button
                                        key={url}
                                        type="button"
                                        onClick={() => setVideo(url)}
                                        className={`w-full flex items-center gap-3 p-3 border-2 transition text-left ${
                                            active
                                                ? "border-[#E63B26] bg-[#FDF9EF]"
                                                : "border-transparent hover:border-black/40"
                                        }`}
                                        data-testid={`edit-video-${allVideos.indexOf(url)}`}
                                    >
                                        <Video className="h-5 w-5 shrink-0 text-[#E63B26]" />
                                        <span className="font-mono text-xs truncate flex-1">
                                            {url}
                                        </span>
                                        {active && (
                                            <span className="flex items-center gap-1 text-[#E63B26] text-xs font-poppins tracking-[0.12em] uppercase">
                                                <Check className="h-3 w-3" /> Selected
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                <DialogFooter className="mt-6 flex-shrink-0">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        className="rounded-none"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        className="bg-[#E63B26] hover:bg-[#1B1B1B] text-white rounded-none"
                        data-testid="edit-submission-save"
                    >
                        Save changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
